import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

export default function AdminSettings() {
  const { refresh } = useSettings();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <div className="text-slate-400">Loading…</div>;

  const upd = (section, key) => (e) => {
    const v = e?.target ? e.target.value : e;
    setS((prev) => ({ ...prev, [section]: { ...prev[section], [key]: v } }));
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings", s); await refresh(); toast.success("Settings saved — live site updated"); }
    catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  const F = ({ label, children }) => <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;

  return (
    <div>
      <SEO title="Settings | Admin" />
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-2xl font-bold tracking-tight">Settings</h1><p className="text-slate-400 text-sm">Control the live website — no code required.</p></div>
        <Button onClick={save} disabled={saving} data-testid="settings-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save changes</>}</Button>
      </div>

      <Tabs defaultValue="brand" className="mt-6">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="brand">Brand</TabsTrigger>
          <TabsTrigger value="homepage">Homepage</TabsTrigger>
          <TabsTrigger value="contact">Contact</TabsTrigger>
          <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
          <TabsTrigger value="seo">SEO</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="social">Social</TabsTrigger>
          <TabsTrigger value="announce">Announcement</TabsTrigger>
        </TabsList>

        <div className="mt-5 rounded-xl border border-white/10 bg-white/5 p-6 max-w-2xl">
          <TabsContent value="brand" className="space-y-4 mt-0">
            <F label="Brand name"><Input value={s.brand?.name || ""} onChange={upd("brand", "name")} data-testid="set-brand-name" /></F>
            <F label="Tagline"><Input value={s.brand?.tagline || ""} onChange={upd("brand", "tagline")} /></F>
            <F label="Footer about text"><Textarea rows={3} value={s.footer?.about || ""} onChange={upd("footer", "about")} /></F>
          </TabsContent>

          <TabsContent value="homepage" className="space-y-4 mt-0">
            <F label="Hero headline"><Textarea rows={2} value={s.homepage?.hero_headline || ""} onChange={upd("homepage", "hero_headline")} data-testid="set-hero-headline" /></F>
            <F label="Hero subheadline"><Textarea rows={3} value={s.homepage?.hero_subheadline || ""} onChange={upd("homepage", "hero_subheadline")} /></F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Primary CTA"><Input value={s.homepage?.primary_cta || ""} onChange={upd("homepage", "primary_cta")} /></F>
              <F label="Secondary CTA"><Input value={s.homepage?.secondary_cta || ""} onChange={upd("homepage", "secondary_cta")} /></F>
            </div>
            <F label="Trust line"><Textarea rows={2} value={s.homepage?.trust_line || ""} onChange={upd("homepage", "trust_line")} /></F>
          </TabsContent>

          <TabsContent value="contact" className="space-y-4 mt-0">
            <F label="Phone"><Input value={s.contact?.phone || ""} onChange={upd("contact", "phone")} data-testid="set-phone" /></F>
            <F label="Email"><Input value={s.contact?.email || ""} onChange={upd("contact", "email")} /></F>
            <F label="Address"><Input value={s.contact?.address || ""} onChange={upd("contact", "address")} /></F>
          </TabsContent>

          <TabsContent value="whatsapp" className="space-y-4 mt-0">
            <F label="WhatsApp number (digits only, incl. country code)"><Input value={s.whatsapp?.number || ""} onChange={upd("whatsapp", "number")} data-testid="set-wa-number" /></F>
            <F label="Default pre-filled message"><Textarea rows={2} value={s.whatsapp?.default_message || ""} onChange={upd("whatsapp", "default_message")} /></F>
            <div className="flex items-center gap-3"><Switch checked={!!s.whatsapp?.enabled} onCheckedChange={upd("whatsapp", "enabled")} /><Label>Show floating WhatsApp button</Label></div>
          </TabsContent>

          <TabsContent value="seo" className="space-y-4 mt-0">
            <F label="Default meta title"><Input value={s.seo?.default_title || ""} onChange={upd("seo", "default_title")} /></F>
            <F label="Default meta description"><Textarea rows={2} value={s.seo?.default_description || ""} onChange={upd("seo", "default_description")} /></F>
            <F label="Keywords"><Input value={s.seo?.keywords || ""} onChange={upd("seo", "keywords")} /></F>
            <F label="OG image URL"><Input value={s.seo?.og_image || ""} onChange={upd("seo", "og_image")} /></F>
            <F label="Robots"><Input value={s.seo?.robots || ""} onChange={upd("seo", "robots")} placeholder="index, follow" /></F>
          </TabsContent>

          <TabsContent value="analytics" className="space-y-4 mt-0">
            <p className="text-xs text-slate-400">Tags load only after visitors accept cookies.</p>
            <F label="Google Analytics 4 ID"><Input value={s.analytics?.ga4_id || ""} onChange={upd("analytics", "ga4_id")} placeholder="G-XXXXXXX" data-testid="set-ga4" /></F>
            <F label="Meta Pixel ID"><Input value={s.analytics?.meta_pixel_id || ""} onChange={upd("analytics", "meta_pixel_id")} /></F>
            <F label="Google Ads conversion ID"><Input value={s.analytics?.google_ads_id || ""} onChange={upd("analytics", "google_ads_id")} placeholder="AW-XXXXXXXXX" data-testid="set-google-ads-id" /></F>
            <F label="Google Ads conversion label"><Input value={s.analytics?.google_ads_conversion_label || ""} onChange={upd("analytics", "google_ads_conversion_label")} placeholder="abCdEfGh123" data-testid="set-google-ads-label" /></F>
            <F label="Search Console verification"><Input value={s.analytics?.search_console_verification || ""} onChange={upd("analytics", "search_console_verification")} /></F>
          </TabsContent>

          <TabsContent value="social" className="space-y-4 mt-0">
            <F label="YouTube"><Input value={s.social?.youtube || ""} onChange={upd("social", "youtube")} /></F>
            <F label="Instagram"><Input value={s.social?.instagram || ""} onChange={upd("social", "instagram")} /></F>
            <F label="Twitter / X"><Input value={s.social?.twitter || ""} onChange={upd("social", "twitter")} /></F>
            <F label="LinkedIn"><Input value={s.social?.linkedin || ""} onChange={upd("social", "linkedin")} /></F>
          </TabsContent>

          <TabsContent value="announce" className="space-y-4 mt-0">
            <div className="flex items-center gap-3"><Switch checked={!!s.announcement?.enabled} onCheckedChange={upd("announcement", "enabled")} data-testid="set-announce" /><Label>Show announcement bar</Label></div>
            <F label="Announcement text"><Input value={s.announcement?.text || ""} onChange={upd("announcement", "text")} /></F>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
